import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Text,
  VStack,
  Flex,
  Spinner,
  keyframes,
  useColorModeValue,
} from '@chakra-ui/react';
import { getYearData } from '../services/yearService';
import { logger } from '../utils/logger';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from 'recharts';

const MotionBox = motion(Box);

// Shimmer animation for the gauge
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/**
 * MarketDemandWidget - Displays a semi-circle gauge showing whether last month
 * was busier or slower compared to the month before that (based on review counts).
 * The gauge shows: BUSIER (green) / STEADY (orange) / SLOWER (red)
 */
const MarketDemandWidget = () => {
  const [yearData, setYearData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const yearResult = await getYearData();
        
        if (mounted) {
          setYearData(yearResult || []);
          setIsLoading(false);
        }
      } catch (error) {
        logger.error('Error fetching data for Market Demand:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  // Calculate month-over-month comparison (last month vs the month before)
  const demandMetrics = useMemo(() => {
    if (!yearData || yearData.length === 0) {
      return { 
        score: 50, 
        change: 0, 
        label: 'Steady',
        totalCurrentReviews: 0,
        totalPrevReviews: 0,
        totalBusinesses: 0,
      };
    }

    // Get last month and the month before that
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthKey = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
    const monthBeforeLast = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const monthBeforeLastKey = `${monthBeforeLast.getFullYear()}-${String(monthBeforeLast.getMonth() + 1).padStart(2, '0')}`;

    // Calculate total reviews for each month across all businesses
    let totalLastMonthReviews = 0;
    let totalMonthBeforeReviews = 0;

    yearData.forEach(business => {
      const monthlyData = business.data?.monthlyReviewCounts || {};
      totalLastMonthReviews += monthlyData[lastMonthKey] || 0;
      totalMonthBeforeReviews += monthlyData[monthBeforeLastKey] || 0;
    });

    // Calculate percentage change from month before to last month
    const percentChange = totalMonthBeforeReviews > 0 
      ? ((totalLastMonthReviews - totalMonthBeforeReviews) / totalMonthBeforeReviews) * 100 
      : 0;

    // Convert percentage change to a 0-100 gauge score
    // -50% or worse maps to 0, +50% or better maps to 100, 0% maps to 50
    // This gives a nice range where the gauge clearly shows busier vs slower
    const gaugeScore = Math.min(100, Math.max(0, 50 + percentChange));

    // Determine label based on the percentage change
    let label;
    if (percentChange > 5) {
      label = 'Busier';
    } else if (percentChange < -5) {
      label = 'Slower';
    } else {
      label = 'Steady';
    }

    return {
      score: Math.round(gaugeScore),
      change: Math.round(percentChange),
      label,
      totalCurrentReviews: totalLastMonthReviews,
      totalPrevReviews: totalMonthBeforeReviews,
      totalBusinesses: yearData.length,
    };
  }, [yearData]);

  // Get score color info (red to green)
  const getScoreColor = (s) => {
    if (s >= 80) return { main: '#22C55E', bg: 'rgba(34, 197, 94, 0.1)' };   // Green
    if (s >= 60) return { main: '#84CC16', bg: 'rgba(132, 204, 22, 0.1)' };  // Lime
    if (s >= 40) return { main: '#EAB308', bg: 'rgba(234, 179, 8, 0.1)' };   // Yellow
    if (s >= 20) return { main: '#F97316', bg: 'rgba(249, 115, 22, 0.1)' };  // Orange
    return { main: '#EF4444', bg: 'rgba(239, 68, 68, 0.1)' };                // Red
  };

  const scoreInfo = getScoreColor(demandMetrics.score);

  // Create gauge data for the pie chart (red to green gradient)
  const gaugeData = [
    { name: 'Low', value: 20, fill: '#EF4444' },      // Red
    { name: 'Slow', value: 20, fill: '#F97316' },     // Orange
    { name: 'Moderate', value: 20, fill: '#EAB308' }, // Yellow
    { name: 'Healthy', value: 20, fill: '#84CC16' },  // Lime
    { name: 'Surging', value: 20, fill: '#22C55E' },  // Green
  ];

  // Calculate the angle for the needle (0 score = -90deg, 100 score = 90deg)
  const needleAngle = -90 + (demandMetrics.score / 100) * 180;

  // Loading state
  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={4}
        h="340px"
        boxShadow="sm"
      >
        <Flex align="center" justify="center" h="100%">
          <VStack spacing={3}>
            <Spinner size="lg" color="orange.500" thickness="3px" />
            <Text color={subtitleColor} fontSize="sm">Loading market demand...</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      bg={cardBg}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={borderColor}
      p={4}
      boxShadow="sm"
      position="relative"
      overflow="hidden"
      h="340px"
    >
      {/* Shimmer effect for high scores */}
      {demandMetrics.score >= 70 && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="4px"
          sx={{
            background: `linear-gradient(90deg, transparent, ${scoreInfo.main}50, transparent)`,
            backgroundSize: '200% 100%',
            animation: `${shimmer} 2s infinite linear`,
          }}
        />
      )}

      <Flex direction="column" h="100%" overflow="hidden">
        {/* Title */}
        <Text fontSize="1.3em" fontWeight="700" color="gray.800" mb={1} flexShrink={0} textTransform="uppercase" fontStyle="italic">
          Last Month
        </Text>

        {/* Main Content - Gauge on Left, Data on Right */}
        <Flex flex="1" w="100%" align="center" gap={3} minH={0}>
          {/* Left Side - Gauge and Score */}
          <Flex flex="1" direction="column" align="center" justify="center" h="100%" minW={0}>
            <Box 
              position="relative" 
              height="224px" 
              width="100%"
              maxW="336px"
              mt="-5px"
              sx={{
                filter: 'drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.1))',
              }}
            >
              {/* The gauge arc */}
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={gaugeData}
                    cx="50%"
                    cy="85%"
                    startAngle={180}
                    endAngle={0}
                    innerRadius={84}
                    outerRadius={133}
                    paddingAngle={2}
                    cornerRadius={5}
                    dataKey="value"
                    stroke="none"
                  >
                    {gaugeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} opacity={0.9} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              {/* Needle */}
              <Box
                position="absolute"
                bottom="36px"
                left="50%"
                transformOrigin="bottom center"
                sx={{
                  transform: `translateX(-50%) rotate(${needleAngle}deg)`,
                  transition: 'transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                <Box
                  width="3px"
                  height="77px"
                  bg={`linear-gradient(to top, ${scoreInfo.main}, transparent)`}
                  borderRadius="full"
                />
              </Box>
            </Box>

            {/* Simplified Status Indicator */}
            <Flex 
              justify="center" 
              align="center"
              mt="-20px"
              w="100%"
            >
              <Text
                fontSize="lg"
                fontWeight="bold"
                color={
                  demandMetrics.change < -5 ? 'red.500' 
                  : demandMetrics.change > 5 ? 'green.500' 
                  : 'orange.500'
                }
                textTransform="uppercase"
              >
                {demandMetrics.change < -5 ? 'Slower' 
                  : demandMetrics.change > 5 ? 'Busier' 
                  : 'Steady'}
              </Text>
            </Flex>
          </Flex>

          {/* Right Side - Data Stats in bubbles */}
          <VStack w="140px" flexShrink={0} align="stretch" spacing={1.5} justify="center">
            <Box
              bg="gray.50"
              borderRadius="md"
              px={2}
              py={1.5}
              boxShadow="sm"
              borderWidth="1px"
              borderColor="gray.100"
            >
              <Text fontSize="2xs" color={subtitleColor} textTransform="uppercase" fontWeight="semibold" lineHeight="1.2">
                Reviews Last Month
              </Text>
              <Text fontSize="md" fontWeight="bold" color="gray.700">
                {demandMetrics.totalCurrentReviews.toLocaleString()}
              </Text>
            </Box>
            <Box
              bg="gray.50"
              borderRadius="md"
              px={2}
              py={1.5}
              boxShadow="sm"
              borderWidth="1px"
              borderColor="gray.100"
            >
              <Text fontSize="2xs" color={subtitleColor} textTransform="uppercase" fontWeight="semibold" lineHeight="1.2">
                Reviews Month Before
              </Text>
              <Text fontSize="md" fontWeight="bold" color="gray.700">
                {demandMetrics.totalPrevReviews.toLocaleString()}
              </Text>
            </Box>
          </VStack>
        </Flex>


      </Flex>
    </MotionBox>
  );
};

export default MarketDemandWidget;
